import React, { useState } from "react";
import styled from "styled-components";
import { Play, X } from "lucide-react";
import trainerPlaceholder from "../assets/trainer-placeholder.png";

const List = styled.div` display: grid; gap: 12px; `;
const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: black;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LightboxContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 0 16px; 
`;

const LightboxImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 9999px; /* rounded-full */
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 100000;
  color: white;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

// --- Helpers ---
const getYouTubeId = (url: string): string => {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    const id = u.searchParams.get("v");
    if (id) return id;
    const parts = u.pathname.split("/");
    const i = parts.indexOf("embed");
    if (i >= 0 && parts[i + 1]) return parts[i + 1];
  } catch { }
  return "";
};

const getInstagramId = (url: string): string => {
  try {
    const u = new URL(url);
    if (u.hostname.includes("instagram.com")) {
      const parts = u.pathname.split("/");
      const pIndex = parts.indexOf("p");
      const reelIndex = parts.indexOf("reel");
      if (pIndex >= 0 && parts[pIndex + 1]) return parts[pIndex + 1];
      if (reelIndex >= 0 && parts[reelIndex + 1]) return parts[reelIndex + 1];
    }
  } catch { }
  return "";
};

const isVideoUrl = (url: string): boolean => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.m4v'];
  let pathToCheck = url;
  try {
    pathToCheck = new URL(url).pathname;
  } catch {
    pathToCheck = url.split('?')[0].split('#')[0];
  }
  const isVideoFile = videoExtensions.some(ext => pathToCheck.toLowerCase().endsWith(ext));
  const isVideoPlatform = url.includes("youtube.com") || url.includes("youtu.be") || url.includes("instagram.com");
  return isVideoFile || isVideoPlatform;
};

const getVideoThumbnail = (url: string): string => {
  const youtubeId = getYouTubeId(url);
  if (youtubeId) {
    return `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`;
  }
  // Instagram doesn't provide direct thumbnail URLs, use placeholder
  if (getInstagramId(url)) {
    return trainerPlaceholder;
  }
  return trainerPlaceholder;
};

// --- Components ---
const VideoThumbnail: React.FC<{
  videoUrl: string;
  onClick: (url: string) => void;
}> = ({ videoUrl, onClick }) => {
  return (
    <div
      className="relative w-full h-[200px] bg-gray-200 overflow-hidden rounded-lg cursor-pointer"
      onClick={() => onClick(videoUrl)}
    >
      <video
        src={videoUrl}
        className="w-full h-full object-cover"
        preload="metadata"
        muted
        playsInline
        onError={(e) => {
          (e.target as HTMLVideoElement).style.display = 'none';
        }}
      />
      <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none">
        <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
          <Play className="w-6 h-6 text-white ml-1" />
        </div>
      </div>
    </div>
  );
};

export type MediaItem =
  | { type: "video"; url: string; title?: string }
  | { type: "image"; src: string; title?: string };

export default function MediaGallery({
  items,
}: {
  items: MediaItem[];
}) {
  const [lightboxMedia, setLightboxMedia] = useState<string | null>(null);
  const [lightboxType, setLightboxType] = useState<"image" | "video" | null>(null);

  const handleMediaClick = (m: MediaItem) => {
    if (m.type === "video") {
      const url = m.url;
      if (url.includes("youtube.com") || url.includes("youtu.be") || url.includes("instagram.com")) {
        window.open(url, '_blank');
      } else {
        setLightboxMedia(url);
        setLightboxType("video");
      }
    } else {
      setLightboxMedia(m.src);
      setLightboxType("image");
    }
  };

  const closeLightbox = () => {
    setLightboxMedia(null);
    setLightboxType(null);
  };

  return (
    <>
      <List>
        {items.map((m, i) => {
          const isVideo = m.type === "video";

          if (isVideo) {
            const url = m.url;
            // Check if it's a local video file
            if (isVideoUrl(url) && !url.includes("youtube.com") && !url.includes("youtu.be") && !url.includes("instagram.com")) {
              return (
                <VideoThumbnail
                  key={i}
                  videoUrl={url}
                  onClick={() => handleMediaClick(m)}
                />
              );
            }

            // External video or unknown
            const thumb = getVideoThumbnail(url);
            return (
              <div
                key={i}
                className="relative w-full h-[200px] bg-gray-200 overflow-hidden rounded-lg cursor-pointer"
                onClick={() => handleMediaClick(m)}
              >
                <img
                  src={thumb}
                  alt={m.title || "Video"}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = trainerPlaceholder;
                  }}
                />
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none">
                  <div className="w-12 h-12 rounded-full bg-[#EB2726] flex items-center justify-center">
                    <Play className="w-6 h-6 text-white ml-1" />
                  </div>
                </div>
              </div>
            );
          } else {
            // Image
            return (
              <div
                key={i}
                className="relative w-full h-[200px] bg-gray-200 overflow-hidden rounded-lg cursor-pointer"
                onClick={() => handleMediaClick(m)}
              >
                <img
                  src={m.src}
                  alt={m.title || "Image"}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = trainerPlaceholder;
                  }}
                />
              </div>
            );
          }
        })}
      </List>

      {/* Lightbox */}
      {lightboxMedia && (
        <LightboxOverlay onClick={closeLightbox}>
          <CloseButton onClick={(e) => {
            e.stopPropagation();
            closeLightbox();
          }}>
            <X size={24} />
          </CloseButton>
          <LightboxContent onClick={(e) => e.stopPropagation()}>
            {lightboxType === "video" ? (
              <video
                src={lightboxMedia}
                controls
                autoPlay
                className="max-w-full max-h-full"
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <LightboxImage
                src={lightboxMedia}
                alt="Preview"
              />
            )}
          </LightboxContent>
        </LightboxOverlay>
      )}
    </>
  );
}
